import { f as formatCurrency } from './utils2.js';

var PayoutStatuses;
(function (PayoutStatuses) {
    PayoutStatuses["paid"] = "paid";
    PayoutStatuses["failed"] = "failed";
    PayoutStatuses["forwarded"] = "forwarded";
    PayoutStatuses["scheduled"] = "scheduled";
    PayoutStatuses["in_transit"] = "in_transit";
    PayoutStatuses["canceled"] = "canceled";
})(PayoutStatuses || (PayoutStatuses = {}));
var PayoutStatusesSafeNames;
(function (PayoutStatusesSafeNames) {
    PayoutStatusesSafeNames["paid"] = "Paid";
    PayoutStatusesSafeNames["failed"] = "Failed";
    PayoutStatusesSafeNames["forwarded"] = "Forwarded";
    PayoutStatusesSafeNames["scheduled"] = "Scheduled";
    PayoutStatusesSafeNames["in_transit"] = "In Transit";
    PayoutStatusesSafeNames["canceled"] = "Canceled";
})(PayoutStatusesSafeNames || (PayoutStatusesSafeNames = {}));
class Payout {
    constructor(payout) {
        this.id = payout.id;
        this.account_id = payout.account_id;
        this.currency = payout.currency;
        this.amount = payout.amount;
        this.bank_account = payout.bank_account;
        this.delivery_method = payout.delivery_method || 'standard';
        this.description = payout.description;
        this.deposits_at = payout.deposits_at;
        this.fees_total = payout.fees_total;
        this.refunds_count = payout.refunds_count;
        this.refunds_total = payout.refunds_total;
        this.payments_count = payout.payments_count;
        this.payments_total = payout.payments_total;
        this.payout_type = payout.payout_type;
        this.other_total = payout.other_total;
        this.status = payout.status;
        this.metadata = payout.metadata;
        this.created_at = payout.created_at;
        this.updated_at = payout.updated_at;
    }
    formattedPaymentAmount(amount) {
        return formatCurrency(amount, this.currency);
    }
}

var ITerminalStatus;
(function (ITerminalStatus) {
    ITerminalStatus["connected"] = "connected";
    ITerminalStatus["disconnected"] = "disconnected";
    ITerminalStatus["unknown"] = "unknown";
    ITerminalStatus["pending_configuration"] = "pending_configuration";
})(ITerminalStatus || (ITerminalStatus = {}));
var TerminalProviders;
(function (TerminalProviders) {
    TerminalProviders["verifone"] = "verifone";
})(TerminalProviders || (TerminalProviders = {}));
class Terminal {
    constructor(data) {
        this.id = data.id || '';
        this.status = data.status;
        this.account_id = data.account_id || '';
        this.platform_account_id = data.platform_account_id || '';
        this.provider = data.provider || null;
        this.provider_id = data.provider_id || '';
        this.provider_serial_number = data.provider_serial_number || '';
        this.created_at = data.created_at || '';
        this.updated_at = data.updated_at || '';
        this.verified_at = data.verified_at || '';
        this.nickname = data.nickname || '';
        this.model_name = data.model_name || null;
    }
}

var TerminalOrderType;
(function (TerminalOrderType) {
    TerminalOrderType["boardingOnly"] = "boarding_only";
    TerminalOrderType["boardingShipping"] = "boarding_shipping";
})(TerminalOrderType || (TerminalOrderType = {}));
var TerminalOrderStatus;
(function (TerminalOrderStatus) {
    TerminalOrderStatus["created"] = "created";
    TerminalOrderStatus["submitted"] = "submitted";
    TerminalOrderStatus["completed"] = "completed";
    TerminalOrderStatus["in_progress"] = "in_progress";
    TerminalOrderStatus["on_hold"] = "on_hold";
    TerminalOrderStatus["canceled"] = "canceled";
})(TerminalOrderStatus || (TerminalOrderStatus = {}));
class TerminalOrder {
    constructor(data) {
        this.id = data.id;
        this.business_id = data.business_id;
        this.company_name = data.company_name;
        this.sub_account_id = data.sub_account_id;
        this.provider = data.provider || TerminalProviders.verifone;
        this.order_items = [];
        this.order_type = data.order_type;
        this.order_status = data.order_status;
        this.terminals = data.terminals;
        this.created_at = data.created_at;
        this.updated_at = data.updated_at;
    }
    get payload() {
        return {
            business_id: this.business_id,
            sub_account_id: this.sub_account_id,
            order_type: this.order_type,
            order_items: this.order_items,
        };
    }
    get totalQuantity() {
        return this.order_items.reduce((acc, item) => acc + item.quantity, 0);
    }
    updateTerminal(model_name, quantity) {
        // if quantity is 0, remove terminal
        if (quantity === 0) {
            this.order_items = this.order_items.filter((t) => t.model_name !== model_name);
            return;
        }
        const terminal = this.order_items.find((t) => t.model_name === model_name);
        if (terminal) {
            terminal.quantity = quantity;
        }
        else {
            this.order_items.push({ model_name, quantity });
        }
    }
}

var TerminalModelName;
(function (TerminalModelName) {
    TerminalModelName["v400"] = "v400";
    TerminalModelName["p400"] = "p400+";
    TerminalModelName["e285"] = "e285";
})(TerminalModelName || (TerminalModelName = {}));
class TerminalModel {
    constructor(data) {
        this.id = data.id;
        this.model_name = data.model_name;
        this.description = data.description;
        this.image_url = data.image_url;
        this.help_url = data.help_url;
    }
}

var AccountType;
(function (AccountType) {
    AccountType["test"] = "test";
    AccountType["live"] = "live";
})(AccountType || (AccountType = {}));
var AccountStatus;
(function (AccountStatus) {
    AccountStatus["created"] = "created";
    AccountStatus["submitted"] = "submitted";
    AccountStatus["information_needed"] = "information_needed";
    AccountStatus["rejected"] = "rejected";
    AccountStatus["enabled"] = "enabled";
    AccountStatus["disabled"] = "disabled";
    AccountStatus["archived"] = "archived";
})(AccountStatus || (AccountStatus = {}));
class SubAccount {
    constructor(subAccount) {
        this.id = subAccount.id;
        this.name = subAccount.name;
        this.account_type = subAccount.account_type;
        this.status = subAccount.status;
        this.currency = subAccount.currency;
        this.platform_account_id = subAccount.platform_account_id;
        this.payout_account_id = subAccount.payout_account_id;
        this.business_id = subAccount.business_id;
        this.application_fee_rates = subAccount.application_fee_rates;
        this.processing_ready = subAccount.processing_ready;
        this.payout_ready = subAccount.payout_ready;
        this.related_accounts = subAccount.related_accounts;
        this.created_at = subAccount.created_at;
        this.updated_at = subAccount.updated_at;
    }
}

var RefundStatuses;
(function (RefundStatuses) {
    RefundStatuses["succeeded"] = "succeeded";
    RefundStatuses["failed"] = "failed";
})(RefundStatuses || (RefundStatuses = {}));
var RefundReasons;
(function (RefundReasons) {
    RefundReasons["customerRequest"] = "customer_request";
    RefundReasons["duplicate"] = "duplicate";
    RefundReasons["fraudulent"] = "fraudulent";
})(RefundReasons || (RefundReasons = {}));
class RefundPayload {
    constructor(refundPayload) {
        this.amount = refundPayload.amount;
        this.reason = refundPayload.reason;
        this.description = refundPayload.description;
        this.metadata = refundPayload.metadata;
    }
}

export { ITerminalStatus as I, Payout as P, RefundReasons as R, SubAccount as S, TerminalOrderStatus as T, TerminalOrderType as a, Terminal as b, TerminalOrder as c, TerminalModel as d, RefundPayload as e };
